import { useMutation } from '@apollo/client';
import { gql } from '__generated__/gql';
import {
  UnitTransferSignatureDocumentTypeEnumType,
  UnitTransferSignatureStatusEnumType,
  UnitTransferSignerRoleEnumType,
  UnitTransferSignerStatusEnumType,
  UnitTransferSigningModeEnumType,
} from '__generated__/graphql';
import dayjs from 'dayjs';
import { FC } from 'react';

import Badge from 'primitives/Badge';
import Button from 'primitives/Button';
import Select from 'primitives/Select';

import ErrorMessage from 'components/ErrorMessage';
import SectionCard from 'components/SectionCard';

import unitTransferSignatureDocumentTypeToReadable from 'utils/enums/unit-transfer-signature-document-type-to-readable';
import unitTransferSignatureStatusToReadable from 'utils/enums/unit-transfer-signature-status-to-readable';
import unitTransferSignerRoleToReadable from 'utils/enums/unit-transfer-signer-role-to-readable';
import unitTransferSignerStatusToReadable from 'utils/enums/unit-transfer-signer-status-to-readable';
import unitTransferSigningModeToReadable from 'utils/enums/unit-transfer-signing-mode-to-readable';

import UploadManuallySignedDocumentDialogButton from './UploadManuallySignedDocumentDialogButton';

const SEND_UNIT_TRANSFER_SIGNATURE_REQUEST_MUTATION = gql(`
  mutation SendUnitTransferSignatureRequest($signatureRequestId: ID!) {
    sendUnitTransferSignatureRequest(signatureRequestId: $signatureRequestId) {
      id
      status
      externalStatus
      sentAt
      signers {
        id
        status
        signingLinkUrl
      }
    }
  }
`);

const RESEND_UNIT_TRANSFER_SIGNATURE_NOTIFICATION_MUTATION = gql(`
  mutation ResendUnitTransferSignatureNotification($signatureRequestId: ID!) {
    resendUnitTransferSignatureNotification(signatureRequestId: $signatureRequestId) {
      id
      status
    }
  }
`);

const SYNC_UNIT_TRANSFER_SIGNATURE_STATUS_MUTATION = gql(`
  mutation SyncUnitTransferSignatureStatus($signatureRequestId: ID!) {
    syncUnitTransferSignatureStatus(signatureRequestId: $signatureRequestId) {
      id
      status
      externalStatus
      completedAt
      signedFileUrl
      signers {
        id
        status
        signedAt
      }
    }
  }
`);

const SET_UNIT_TRANSFER_SIGNATURE_REQUEST_SIGNING_MODE_MUTATION = gql(`
  mutation SetUnitTransferSignatureRequestSigningMode($signatureRequestId: ID!, $mode: UnitTransferSigningModeEnumType!) {
    setUnitTransferSignatureRequestSigningMode(signatureRequestId: $signatureRequestId, mode: $mode) {
      id
      signingMode
    }
  }
`);

const SIGNING_MODE_OPTIONS = [
  { value: UnitTransferSigningModeEnumType.Digital, label: 'Digital (E-Sign)' },
  { value: UnitTransferSigningModeEnumType.Manual, label: 'Manual' },
];

type Signer = {
  id: string;
  role: UnitTransferSignerRoleEnumType;
  name: string;
  email: string;
  status: UnitTransferSignerStatusEnumType;
  signedAt?: string | null;
  // signingLinkUrl resolves to null for anyone but the signer it belongs to — ops always sees
  // null here, so this card deliberately has no "copy signing link" affordance. Resend is the
  // only way ops can nudge a signer.
  signingLinkUrl?: string | null;
};

type SignatureRequest = {
  id: string;
  documentType: UnitTransferSignatureDocumentTypeEnumType;
  sequence: number;
  status: UnitTransferSignatureStatusEnumType;
  signingMode: UnitTransferSigningModeEnumType;
  externalStatus?: string | null;
  externalDocumentId?: string | null;
  signedFileUrl?: string | null;
  blankTemplateUrl?: string | null;
  sentAt?: string | null;
  completedAt?: string | null;
  signers: Signer[];
};

const SignatureRequestCard: FC<{
  signatureRequest: SignatureRequest;
  blockedBy: string[];
}> = ({ signatureRequest, blockedBy }) => {
  const [sendRequest, { loading: sending, error: sendError }] = useMutation(
    SEND_UNIT_TRANSFER_SIGNATURE_REQUEST_MUTATION
  );
  const [resendNotification, { loading: resending, error: resendError }] = useMutation(
    RESEND_UNIT_TRANSFER_SIGNATURE_NOTIFICATION_MUTATION
  );
  const [syncStatus, { loading: syncing, error: syncError }] = useMutation(
    SYNC_UNIT_TRANSFER_SIGNATURE_STATUS_MUTATION
  );
  const [setSigningMode, { loading: settingSigningMode, error: signingModeError }] = useMutation(
    SET_UNIT_TRANSFER_SIGNATURE_REQUEST_SIGNING_MODE_MUTATION
  );

  const { label, color } = unitTransferSignatureStatusToReadable(signatureRequest.status);
  const isManual = signatureRequest.signingMode === UnitTransferSigningModeEnumType.Manual;

  // A row marked Sent with no externalDocumentId never actually reached Leegality — the create call
  // returned without a document id. Nothing exists to sync against or resend to, so Send is the only
  // recovery, and hiding it (as "already sent") strands the whole transfer with no way forward from
  // the UI. The backend permits the retry for the same reason. Doesn't apply to manual mode, which
  // never has an externalDocumentId at all.
  const neverReachedLeegality =
    !isManual &&
    signatureRequest.status !== UnitTransferSignatureStatusEnumType.NotSent &&
    !signatureRequest.externalDocumentId;

  const canSend =
    (signatureRequest.status === UnitTransferSignatureStatusEnumType.NotSent ||
      neverReachedLeegality) &&
    blockedBy.length === 0;
  // Resend/Sync only mean anything for the digital (Leegality) path — manual mode has nothing to
  // notify a signer about (they were never emailed) and nothing external to poll.
  const canResend =
    !isManual &&
    (signatureRequest.status === UnitTransferSignatureStatusEnumType.Sent ||
      signatureRequest.status === UnitTransferSignatureStatusEnumType.PartiallySigned);
  const canSync =
    !isManual && signatureRequest.status !== UnitTransferSignatureStatusEnumType.NotSent;
  const canUploadSignedCopy =
    isManual && signatureRequest.status === UnitTransferSignatureStatusEnumType.Sent;
  // Mirrors the backend's BUS_102 guard — once signed, the mode that got it there is history.
  const canChangeSigningMode =
    signatureRequest.status !== UnitTransferSignatureStatusEnumType.Signed;

  return (
    <SectionCard
      title={`${signatureRequest.sequence}. ${unitTransferSignatureDocumentTypeToReadable(
        signatureRequest.documentType
      )}`}
      subTitle={
        signatureRequest.sentAt
          ? `Sent ${dayjs(signatureRequest.sentAt).format('D MMM YYYY, h:mm A')}`
          : undefined
      }
      /* PARTIALLY_SIGNED is a real state — surfaced via signer-level progress below, not just this badge. */
      headerContent={<Badge label={label} color={color} showIndicator />}
      bodyClassName="px-4 py-3 sm:p-4 flex flex-col gap-3"
    >
      <>
        {signatureRequest.status === UnitTransferSignatureStatusEnumType.NotSent &&
          blockedBy.length > 0 && (
            <p className="text-sm text-amber-700">Waiting on: {blockedBy.join(', ')}</p>
          )}

        {canChangeSigningMode ? (
          <Select
            label="Signing mode"
            options={SIGNING_MODE_OPTIONS}
            value={signatureRequest.signingMode}
            readOnly={settingSigningMode}
            onChange={mode =>
              setSigningMode({
                variables: {
                  signatureRequestId: signatureRequest.id,
                  mode: mode as UnitTransferSigningModeEnumType,
                },
              })
            }
          />
        ) : (
          <p className="text-sm text-gray-500">
            Signing mode: {unitTransferSigningModeToReadable(signatureRequest.signingMode).label}{' '}
            (locked — already signed)
          </p>
        )}
        {signingModeError && <ErrorMessage error={signingModeError} type="alert" />}

        <div className="flex flex-col divide-y divide-gray-100">
          {signatureRequest.signers.map(signer => {
            const signerReadable = unitTransferSignerStatusToReadable(signer.status);
            return (
              <div key={signer.id} className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm font-medium text-gray-900">{signer.name}</p>
                  <p className="text-xs text-gray-500">
                    {unitTransferSignerRoleToReadable(signer.role)} · {signer.email}
                  </p>
                </div>
                <Badge label={signerReadable.label} color={signerReadable.color} />
              </div>
            );
          })}
        </div>

        {canUploadSignedCopy && signatureRequest.blankTemplateUrl && (
          <a
            className="text-sm text-blue-500 hover:underline w-fit"
            href={signatureRequest.blankTemplateUrl}
            target="_blank"
            rel="noreferrer"
          >
            Download Blank Template
          </a>
        )}

        {signatureRequest.signedFileUrl && (
          <a
            className="text-sm text-blue-500 hover:underline w-fit"
            href={signatureRequest.signedFileUrl}
            target="_blank"
            rel="noreferrer"
          >
            View Signed Document
          </a>
        )}

        <div className="flex flex-row gap-2 items-center">
          {canSend && (
            <Button
              loading={sending}
              onClick={() =>
                sendRequest({ variables: { signatureRequestId: signatureRequest.id } })
              }
            >
              Send
            </Button>
          )}
          {canResend && (
            <Button
              variant="secondary"
              loading={resending}
              onClick={() =>
                resendNotification({ variables: { signatureRequestId: signatureRequest.id } })
              }
            >
              Resend Notification
            </Button>
          )}
          {canSync && (
            <Button
              variant="secondary"
              loading={syncing}
              onClick={() => syncStatus({ variables: { signatureRequestId: signatureRequest.id } })}
            >
              Sync Status
            </Button>
          )}
          {/* Ops fallback for when a signer hands ops a physical/scanned copy directly — the
              investor's own upload happens from investor-app, same mutation either way. */}
          {canUploadSignedCopy && (
            <UploadManuallySignedDocumentDialogButton signatureRequestId={signatureRequest.id} />
          )}
        </div>
        {sendError && <ErrorMessage error={sendError} type="alert" />}
        {resendError && <ErrorMessage error={resendError} type="alert" />}
        {syncError && <ErrorMessage error={syncError} type="alert" />}
      </>
    </SectionCard>
  );
};

export default SignatureRequestCard;
